import { getAvailableBodyTypes, getAvailableBrands, getAvailableBudgetPriceRanges, getAvailableFuelTypes, getAvailableSeatingCapacity } from "@/app/utilities/helper";
import CarFilterClient from "./CarFilterClient";
import { FilterConfig } from "./Types";
import { getFilterOptions } from "@/app/utilities/apis";

// filter types for api calling for filter dropdowns 
type DefaultFilters = {
	is_latest?: boolean;
	is_popular?: boolean;
	is_upcoming?: boolean;
	min_price?: string;
	max_price?: string;

	// filters
	price?: string;
	body_type?: string | string[];
	fuel_type?: string | string[];
	transmission?: string | string[];
	mileage?: string | string[];
	range?: string | string[];
	seating_capacity?: string | string[];
	brand?: string | string[];
}

type CarFilterProps = {
	defaultFilters?: DefaultFilters;
	stickyOnScroll?: boolean;
	clearAllTrackingClass?: string;
	applyFilterTrackingClass?: string;
	sortAppliedTrackingClass?: string;
}

export const FILTER_KEYS = {
	PRICE: "price",
	BODY_TYPE: "body_type",
	FUEL_TYPE: "fuel_type",
	TRANSMISSION: "transmission",
	BRAND: "brand",
	MILEAGE: "mileage",
	RANGE: "range",
	SEATING: "seating_capacity",
	BATTERY_CAPACITY: "battery_capacity",
}

// ─── Base Data ──────────────────────────────────────────────────────────────────
export const BASE_FILTER_CONFIGS: FilterConfig[] = [
	{
		title: "Price",
		key: FILTER_KEYS.PRICE,
		type: "single",
		options: [],
	},
	{
		title: "Body Type",
		key: FILTER_KEYS.BODY_TYPE,
		type: "multi",
		options: [],
	},
	{
		title: "Fuel Type",
		key: FILTER_KEYS.FUEL_TYPE,
		type: "multi",
		options: [],
	},
	{
		title: "Transmission",
		key: FILTER_KEYS.TRANSMISSION,
		type: "multi",
		options: [],
	},
	{
		title: "Brand",
		key: FILTER_KEYS.BRAND,
		type: "multi",
		options: [],
	},
	{
		title: "Mileage",
		key: FILTER_KEYS.MILEAGE,
		type: "multi",
		options: [],
	},
	{
		title: "Range",
		key: FILTER_KEYS.RANGE,
		type: "multi",
		options: [],
	},
	{
		title: "Seating Capacity",
		key: FILTER_KEYS.SEATING,
		type: "multi",
		options: [],
	},
	{
		title: "Battery Capacity",
		key: FILTER_KEYS.BATTERY_CAPACITY,
		type: "multi",
		options: [],
	}
];

export default async function CarFilter({ defaultFilters = {}, stickyOnScroll = false, clearAllTrackingClass = '', applyFilterTrackingClass = '', sortAppliedTrackingClass = '' }: CarFilterProps) {
	const filters = { ...defaultFilters };

	// if price is in range format like 5-10 then convert it to min_price and max_price
	if (filters.price) {
		const [min, max] = filters.price.split("-");
		min && (filters.min_price = min);
		max && (filters.max_price = max);
	}

	const [
		availablePriceRanges,
		availableFuelTypes,
		availableSeatingCapacity,
		availableBodyTypes,
		availableFilterOptions,
		availableBrands
	] = await Promise.all([
		getAvailableBudgetPriceRanges({ ...filters }),
		getAvailableFuelTypes({ ...filters }),
		getAvailableSeatingCapacity({ ...filters }),
		getAvailableBodyTypes({ ...filters }),
		getFilterOptions({ ...filters }),
		getAvailableBrands({ ...filters })
	]);

	// Construct dynamic filterConfigs based on fetched availability
	const filterConfigs = BASE_FILTER_CONFIGS.map(config => {
		if (config.key === FILTER_KEYS.PRICE && availablePriceRanges) {
			const activeOptions = Object.entries(availablePriceRanges)?.map(([key, value]: [string, any]) => ({
				label: value.label,
				value: `${value.min}${value.max ? `-${value.max}` : ''}`
			}));
			if (activeOptions.length > 0) {
				return {
					...config,
					options: activeOptions
				};
			}
		}

		if (config.key === FILTER_KEYS.FUEL_TYPE && availableFuelTypes && availableFuelTypes.length > 0) {
			return {
				...config,
				options: availableFuelTypes.map((f: any) => ({
					label: f.label,
					value: f.label
				}))
			};
		}

		if (config.key === FILTER_KEYS.SEATING && availableSeatingCapacity) {
			const seatingOpts = Object.values(availableSeatingCapacity).map((k: any) => ({
				label: `${k.label}`,
				value: `${k.value}`
			}));
			if (seatingOpts.length > 0) {
				return {
					...config,
					options: seatingOpts
				};
			}
		}

		if (config.key === FILTER_KEYS.BODY_TYPE && availableBodyTypes && availableBodyTypes.length > 0) {
			return {
				...config,
				options: availableBodyTypes.map((b: any) => ({
					label: b.label,
					value: b.value
				}))
			};
		}

		if (config.key === FILTER_KEYS.MILEAGE && availableFilterOptions?.mileage && availableFilterOptions?.mileage.length > 0) {
			return {
				...config,
				options: availableFilterOptions?.mileage.map((m: any) => ({
					label: m.label,
					value: `${m.min}-${m.max}`
				}))
			};
		}

		if (config.key === FILTER_KEYS.RANGE && availableFilterOptions?.range && availableFilterOptions?.range.length > 0) {
			return {
				...config,
				options: availableFilterOptions?.range.map((m: any) => ({
					label: m.label,
					value: `${m.min}-${m.max}`
				}))
			};
		}

		if (config.key === FILTER_KEYS.TRANSMISSION && availableFilterOptions?.transmissions && availableFilterOptions?.transmissions.length > 0) {
			return {
				...config,
				options: availableFilterOptions?.transmissions.map((t: any) => ({
					label: t,
					value: t
				}))
			};
		}

		if (config.key === FILTER_KEYS.BATTERY_CAPACITY && availableFilterOptions?.battery_capacity && availableFilterOptions?.battery_capacity.length > 0) {
			return {
				...config,
				options: availableFilterOptions?.battery_capacity.map((b: any) => ({
					label: b.label,
					value: `${b.min}-${b.max}`
				}))
			};
		}

		if (config.key === FILTER_KEYS.BRAND && availableBrands && availableBrands?.length > 0) {
			return {
				...config,
				options: availableBrands?.sort((a: any, b: any) => a.name.localeCompare(b.name)).map((b: any) => ({
					label: b.name,
					value: b.slug
				}))
			};
		}

		return config;
	}).filter((config: any) => !Object.keys(filters || {}).includes(config.key));

	return <CarFilterClient filterConfigs={filterConfigs} defaultFilters={filters} stickyOnScroll={stickyOnScroll} clearAllTrackingClass={clearAllTrackingClass} applyFilterTrackingClass={applyFilterTrackingClass} sortAppliedTrackingClass={sortAppliedTrackingClass} />;
}
